// src/components/pages/manufacturing_app/reports/OeeDashboard/OeeDashboard.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Row, Col, Button } from "react-bootstrap";
import ApexCharts from "apexcharts";
import "./OeeDashboard.css";
import { getCurrentDate } from "../../../../../common/Validations";
import FilterBox from "../../../../common/FilterBox";
import AnimatedCounter from "../../../../common/AnimatedCounter";
import moment from "moment";
import ItemService from "../../../../../services/commonService";

//Redux
import { useSelector } from "react-redux"; //useDispatch

// Images
// import timeManagementImg from "./Images/time-management.png";
// import realTimeImg from "./Images/real-time.png";
import totalProducedImg from "../../../../images/Images/total-produced.png";
import approvedImg from "../../../../images/Images/approved.png";
import rejectedimg from "../../../../images/Images/rejected.png";

// Department rings (inside each card)
// import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { ProgressBar } from "react-bootstrap";

import "react-circular-progressbar/dist/styles.css";

// -----------------------------------------------------------------------------
// Config & helpers
// -----------------------------------------------------------------------------
const gaugeColors = {
  OEE: "#9C27B0",
  availability: "#4CAF50",
  performance: "#FF9800",
  quality: "#03A9F4",
};

const defaultData = {
  totalPlannedProductionTime: 0,
  totalActualOperatingTime: 0,
  totalPlannedProductionTimeDisplay: "0",
  totalActualOperatingTimeDisplay: "0",
  totalProduced: 0,
  totalRejected: 0,
  totalGood: 0,
  availability: "0",
  performance: "0",
  quality: "0",
  OEE: "0",
  deptSummary: [],
};

// Safe number parser
const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const OeeDashboard = () => {
  const [displayFilter, setDisplayFilter] = useState(false);
  const [getOeeData, setOeeData] = useState(defaultData);

  let getAllDepartmentMaster = useSelector(
    (state) => state?.common?.departmentMaster
  );

  const [allDepartmentMaster, setAllDepartmentMaster] = useState([]);

  // Apex gauge refs & instances
  const gaugeRefs = {
    OEE: useRef(null),
    availability: useRef(null),
    performance: useRef(null),
    quality: useRef(null),
  };
  const chartInstances = useRef({
    OEE: null,
    availability: null,
    performance: null,
    quality: null,
  });

  // Numeric sort for DEPT codes: DEPT001 < DEPT010 < DEPT017
  const sortedDeptSummary = useMemo(() => {
    const arr = Array.isArray(getOeeData?.deptSummary)
      ? getOeeData.deptSummary
      : [];
    return [...arr].sort(
      (a, b) =>
        Number(String(a?.dept || "").replace(/\D/g, "")) -
        Number(String(b?.dept || "").replace(/\D/g, ""))
    );
  }, [getOeeData?.deptSummary]);

  // // Initial cricle one radial gauge
  // const createGauge = useCallback((elRef, value, color, label) => {
  //   if (!elRef?.current) return;

  //   // destroy previous if any
  //   if (chartInstances.current[label]) {
  //     chartInstances.current[label].destroy();
  //     chartInstances.current[label] = null;
  //   }

  //   const options = {
  //     series: [value],
  //     chart: {
  //       type: "radialBar",
  //       height: 250,
  //       animations: { enabled: true, easing: "easeinout", speed: 800 },
  //       toolbar: { show: false },
  //     },
  //     plotOptions: {
  //       radialBar: {
  //         hollow: { size: "60%" },
  //         track: { background: "#f4f4f4" },
  //         dataLabels: {
  //           name: { offsetY: -10, color: "#333", fontSize: "14px" },
  //           value: {
  //             color: "#333",
  //             fontSize: "24px",
  //             fontWeight: 600,
  //             offsetY: 5,
  //             formatter: (val) => `${num(val).toFixed(2)}%`,
  //           },
  //         },
  //       },
  //     },
  //     fill: { colors: [color] },
  //     labels: [label],
  //   };

  //   const inst = new ApexCharts(elRef.current, options);
  //   chartInstances.current[label] = inst;
  //   inst.render();
  // }, []);

  // value-aware color (red < 80, amber < 90, green otherwise)
  const pickGaugeColors = (val, baseColor) => {
    const v = Number.isFinite(val) ? val : 0;
    if (v < 80) return { from: "#ef4444", to: "#f97316" }; // red → orange
    if (v < 90) return { from: "#f59e0b", to: "#fbbf24" }; // amber
    return { from: baseColor || "#22c55e", to: "#86efac" }; // green → light green
  };

  // Create one radial gauge (prettier)
  const createGauge = useCallback((elRef, value, color, label) => {
    if (!elRef?.current) return;

    // destroy previous if any
    if (chartInstances.current[label]) {
      chartInstances.current[label].destroy();
      chartInstances.current[label] = null;
    }

    const v = num(value);
    const { from, to } = pickGaugeColors(v, color);

    const options = {
      series: [v],
      chart: {
        type: "radialBar",
        height: 180,
        sparkline: { enabled: true },
        animations: { enabled: true, easing: "easeinout", speed: 900 },
        toolbar: { show: false },
        foreColor: "#334155",
      },
      // Set start/end angle for a modern arc look (semi-circle). For full donut, use -135/225.
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 225,
          hollow: {
            size: "68%",
            background: "transparent",
            dropShadow: {
              enabled: true,
              top: 1,
              blur: 6,
              opacity: 0.15,
            },
          },
          track: {
            background: "rgba(148,163,184,0.15)", // slate-300 @ 15%
            strokeWidth: "100%",
            margin: 0,
            dropShadow: {
              enabled: false,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 22,
              color: "#64748b",
              fontSize: "12px",
              fontWeight: 600,
            },
            value: {
              offsetY: -4,
              color: "#0f172a",
              fontSize: "24px",
              fontWeight: 700,
              formatter: (val) => `${num(val).toFixed(1)}%`,
            },
            total: {
              show: true,
              label: "-",
              color: "#94a3b8",
              fontSize: "11px",
              fontWeight: 600,
            },
          },
        },
      },
      stroke: {
        lineCap: "round", // rounded ends
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "solid",
          type: "horizontal",
          gradientToColors: [to],
          stops: [0, 100],
          colorStops: [
            { offset: 0, color: from, opacity: 1 },
            { offset: 100, color: to, opacity: 1 },
          ],
        },
      },
      labels: [label],
    };

    const inst = new ApexCharts(elRef.current, options);
    chartInstances.current[label] = inst;
    inst.render();
  }, []);

  useEffect(() => {
    setAllDepartmentMaster(getAllDepartmentMaster);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAllDepartmentMaster]);

  // Render / update top gauges when metrics change
  useEffect(() => {
    const instances = chartInstances.current; // snapshot for cleanup

    createGauge(gaugeRefs.OEE, num(getOeeData?.OEE), gaugeColors.OEE, "OEE");
    createGauge(
      gaugeRefs.availability,
      num(getOeeData?.availability),
      gaugeColors.availability,
      "availability"
    );
    createGauge(
      gaugeRefs.performance,
      num(getOeeData?.performance),
      gaugeColors.performance,
      "performance"
    );
    createGauge(
      gaugeRefs.quality,
      num(getOeeData?.quality),
      gaugeColors.quality,
      "quality"
    );

    return () => {
      Object.keys(instances).forEach((k) => {
        instances[k]?.destroy?.();
        instances[k] = null;
      });
    };
  }, [
    createGauge,
    gaugeRefs.OEE,
    gaugeRefs.availability,
    gaugeRefs.performance,
    gaugeRefs.quality,
    getOeeData?.OEE,
    getOeeData?.availability,
    getOeeData?.performance,
    getOeeData?.quality,
  ]);

  // Data fetchers
  const fetchOeeData = useCallback(async (fromDate, toDate) => {
    const obj = { fromDate, toDate };
    const res = await ItemService.oeeDashboardReport(obj).catch(() => null);
    if (res?.status === 200) setOeeData(res);
  }, []);

  const fetchTodaysOeeData = useCallback(async () => {
    const today = moment().format("YYYY-MM-DD");
    await fetchOeeData(today, today);
  }, [fetchOeeData]);

  useEffect(() => {
    fetchTodaysOeeData();
  }, [fetchTodaysOeeData]);

  // FilterBox handlers
  const searchData = async (searchObj) => {
    await fetchOeeData(searchObj?.requiredFrom, searchObj?.requiredTo);
  };
  const resetSearch = () => fetchTodaysOeeData();

  // CircularProgressbar MetricCircle implementation
  // const MetricCircle = ({ title, value, color }) => {
  //   const [progress, setProgress] = useState(0);

  //   useEffect(() => {
  //     let start = 0;
  //     const end = num(value);
  //     const step = end / 50; // animation smoothness
  //     const timer = setInterval(() => {
  //       start += step;
  //       if (start >= end) {
  //         start = end;
  //         clearInterval(timer);
  //       }
  //       setProgress(start);
  //     }, 10);
  //     return () => clearInterval(timer);
  //   }, [value]);

  //   return (
  //     <div
  //       style={{
  //         width: 120,
  //         textAlign: "center",
  //         // background: "linear-gradient(180deg, #ffffff 0%, #f7f8fb 100%)",
  //         borderRadius: "12px",
  //         padding: "8px",
  //         // boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  //         transition: "transform 0.2s",
  //       }}
  //       className="metric-circle"
  //       onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
  //       onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
  //     >
  //       <CircularProgressbar
  //         value={progress}
  //         text={`${progress.toFixed(1)}%`}
  //         styles={buildStyles({
  //           pathColor: `url(#gradient-${color})`,
  //           trailColor: "#e9ecef",
  //           textColor: "#2c3e50",
  //           textSize: "16px",
  //           fontWeight: 600,
  //           pathTransitionDuration: 0.9,
  //         })}
  //       />
  //       {/* Gradient definition for each color */}
  //       <svg style={{ height: 0 }}>
  //         <defs>
  //           <linearGradient
  //             id={`gradient-${color}`}
  //             gradientTransform="rotate(90)"
  //           >
  //             <stop offset="0%" stopColor={color} />
  //             <stop offset="100%" stopColor="#e7e4e4ff" stopOpacity="1" />
  //           </linearGradient>
  //         </defs>
  //       </svg>
  //       <div style={{ marginTop: 8, fontWeight: 600 }}>{title}</div>
  //     </div>
  //   );
  // };

  // horizontal ProgressBar MetricCircle  implementation
  const MetricCircle = ({ title, value, color }) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
      let start = 0;
      const end = num(value);
      const step = end / 50; // controls speed/smoothness
      const timer = setInterval(() => {
        start += step;
        if (start >= end) {
          start = end;
          clearInterval(timer);
        }
        setProgress(start);
      }, 20);
      return () => clearInterval(timer);
    }, [value]);

    return (
      <div
        style={{
          width: "180px",
          textAlign: "left",
          background: "#fff",
          borderRadius: "10px",
          padding: "10px 14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          transition: "transform 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "4px",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#333",
            }}
          >
            {title}
          </span>
          <span
            style={{
              fontSize: "14px",
              fontWeight: 700,
              color: color,
            }}
          >
            {progress.toFixed(1)}%
          </span>
        </div>

        <ProgressBar
          now={progress}
          variant="custom"
          style={{
            height: "10px",
            borderRadius: "8px",
            backgroundColor: "#f0f0f0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: `linear-gradient(90deg, ${color}, #a8a8a8)`,
              transition: "width 0.4s ease",
            }}
          ></div>
        </ProgressBar>
      </div>
    );
  };

  const DeptCard = ({ dept }) => (
    <div
      style={{
        background: "rgb(249 249 249)",
        borderRadius: "12px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        padding: "10px",
        width: "400px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* Department Header */}
      <div className="dept-header">
        <div className="dept-badge">{dept.dept}</div>
        <div className="dept-badge">
          {allDepartmentMaster.find((d) => d.id === dept.dept)?.deptName ||
            ""}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          flexWrap: "wrap",
          gap: 10.2,
        }}
      >
        <MetricCircle title="OEE" value={dept.OEE} color={gaugeColors.OEE} />
        <MetricCircle
          title="Availability"
          value={dept.availability}
          color={gaugeColors.availability}
        />
        <MetricCircle
          title="Performance"
          value={dept.performance}
          color={gaugeColors.performance}
        />
        <MetricCircle
          title="Quality"
          value={dept.quality}
          color={gaugeColors.quality}
        />
      </div>

      {/* Numeric Stats — compact mini cards */}
      <div className="dept-stats">
        <StatMini
          icon="🕒"
          label="Actual Time"
          value={`${num(dept.actualOperatingTime).toFixed(2)}`}
          unit="hrs"
        />
        <StatMini
          icon="📅"
          label="Planned Time"
          value={`${num(dept.plannedProductionTime)}`}
          unit="hrs"
        />
        <StatMini
          icon="✅"
          label="Total Good"
          value={`${num(dept.totalGood)}`}
        />
        <StatMini
          icon="❌"
          label="Rejected"
          value={`${num(dept.totalRejected)}`}
          tone="danger"
        />
        <StatMini
          icon="⚙️"
          label="Produced"
          value={`${num(dept.totalProduced)}`}
          tone="info"
        />
      </div>
    </div>
  );

  const StatMini = ({ icon, label, value, unit, tone = "default" }) => (
    <div className={`stat-mini stat-${tone}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-label">{label}</div>
        <div className="stat-value">
          <span className="stat-number">
            <AnimatedCounter end={value} prefix="" suffix="" duration={3} />
          </span>
          {unit ? <span className="stat-unit">{unit}</span> : null}
        </div>
      </div>
    </div>
  );

  return (
    <main className="generatejobcard main" id="main">
      <Row className="justify-center subContent">
        <Col xs={10} md={8} className="rightSide">
          <div className="rightSideInner timesheet">
            <h2 className="mainTitle">
              OEE Dashboard{" "}
              <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage">
                {getCurrentDate()}
              </span>
            </h2>
            <p className="subText">OEE Dashboard details page</p>
          </div>
        </Col>
        <Col xs={2} md={4} className="leftSide">
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="btn btn-primary float-right searchFilter-btn"
            onClick={() => setDisplayFilter((v) => !v)}
          >
            <i className="bi bi-filter" />{" "}
          </Button>
        </Col>
        <hr />
      </Row>

      {displayFilter && (
        <Row className="reportheader">
          <Col xs={12} md={12} className="leftSide">
            <FilterBox
              searchData={searchData}
              handleDownload={resetSearch}
              resetFilerBox={fetchTodaysOeeData}
              resetSearch={resetSearch}
            />
          </Col>
        </Row>
      )}

      {/* Top Gauges */}
      <Row>
        <Col xs={6} md={3} className="px-2">
          <div className="chart-card">
            <h5 className="text-center">OEE</h5>
            <div ref={gaugeRefs.OEE} />
          </div>
        </Col>
        <Col xs={6} md={3} className="px-2">
          <div className="chart-card">
            <h5 className="text-center">Availability</h5>
            <div ref={gaugeRefs.availability} />
          </div>
        </Col>
        <Col xs={6} md={3} className="px-2">
          <div className="chart-card">
            <h5 className="text-center">Performance</h5>
            <div ref={gaugeRefs.performance} />
          </div>
        </Col>
        <Col xs={6} md={3} className="px-2">
          <div className="chart-card">
            <h5 className="text-center">Quality</h5>
            <div ref={gaugeRefs.quality} />
          </div>
        </Col>
      </Row>

      {/* KPI Section */}
      <Row className="row mt-5">
        {/* {[
          {
            label: "Planned Production Time",
            value: getOeeData?.totalPlannedProductionTimeDisplay,
            color: "#4CAF50",
            image: timeManagementImg,
          },
          {
            label: "Actual Operating Time",
            value: getOeeData?.totalActualOperatingTimeDisplay,
            color: "#FF9800",
            image: realTimeImg,
          },
        ].map((item, index) => (
          <Col xs={12} md={6} className="mb-4 px-2" key={index}>
            <div
              className="kpi-card"
              style={{ borderBottom: `4px solid ${item.color}` }}
            >
              <div className="kpi-content">
                <div>
                  <h5 className="text-muted">{item.label}</h5>
                  <p className="fw-bold oee-fontsize-30">{item.value}</p>
                </div>
                <img src={item.image} alt="KPI Icon" className="kpi-image" />
              </div>
            </div>
          </Col>
        ))} */}

        {[
          {
            label: "Total Produced",
            value: getOeeData?.totalProduced,
            color: "#03A9F4",
            image: totalProducedImg,
          },
          {
            label: "Total Good",
            value: getOeeData?.totalGood,
            color: "#4CAF50",
            image: approvedImg,
          },
          {
            label: "Total Rejected",
            value: getOeeData?.totalRejected,
            color: "#F44336",
            image: rejectedimg,
          },
        ].map((item, index) => (
          <Col xs={12} md={4} className="mb-4 px-2" key={index}>
            <div
              className="kpi-card"
              style={{ borderBottom: `4px solid ${item.color}` }}
            >
              <div className="kpi-content">
                <div>
                  <h5 className="text-muted">{item.label}</h5>
                  <p className="fw-bold oee-fontsize-30">
                    <AnimatedCounter end={num(item.value)} duration={3} />
                  </p>
                </div>
                <img src={item.image} alt="KPI Icon" className="kpi-image" />
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Department-wise OEE Cards */}
      <div className="section-header">
        <h4>Department-wise OEE</h4>
        <div className="section-line"></div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          justifyContent: "center",
          // background: "#f4f6f8",
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 5,
          paddingRight: 5,
        }}
      >
        {sortedDeptSummary.map((dept, idx) => (
          <DeptCard key={`${dept.dept}-${idx}`} dept={dept} />
        ))}
      </div>
    </main>
  );
};

export default OeeDashboard;
